import { Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { CollectionAddMemberFacade } from '@mode/collections/domain';
import { CollectionAccessLevel } from '@mode/shared/contract-common';
import { SpaceDomain } from '@mode/shared/contract-common';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ManageAccessAlertMessage, ManageAccessAlertType } from '@mode/shared/ui';
import { AnalyticsService } from '@mode/shared/util-segment';
import { AccessMenuItem } from '../access-selector/access-selector.component';
import { ChildProcessWithoutNullStreams } from 'node:child_process';

@Component({
  selector: 'mode-add-access-dialog',
  templateUrl: './add-access-dialog.component.html',
  styleUrls: ['./add-access-dialog.component.scss'],
})
export class AddAccessDialogComponent implements OnDestroy {
  addEntitlementsDisabled$: Observable<boolean>;
  isSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedResults$: BehaviorSubject<SpaceDomain.PermissionsSearchResult[]> = new BehaviorSubject(
    [] as SpaceDomain.PermissionsSearchResult[]
  );

  modeAlert!: ManageAccessAlertMessage | ChildProcessWithoutNullStreams | null;

  @Input()
  collectionId!: string;

  @Input()
  workspaceName!: string;

  @Output()
  done = new EventEmitter<boolean>();

  constructor(
    private analyticsService: AnalyticsService,
    private addMemberFacade: CollectionAddMemberFacade,
    private dialog: MatLegacyDialogRef<AddAccessDialogComponent>
  ) {
    this.addMemberFacade.selectedResults$.subscribe(this.selectedResults$);

    this.addEntitlementsDisabled$ = combineLatest([this.selectedResults$, this.isSaving$]).pipe(
      map(([results, saving]) => saving || !results.length)
    );
  }

  ngOnDestroy() {
    this.addMemberFacade.clearSelectedResults();
  }

  removeSelectedEntitlement(entitlement: SpaceDomain.PermissionsSearchResult) {
    this.addMemberFacade.removeSelectedResult(entitlement);
  }

  entitlementChange(menuItem: Partial<AccessMenuItem>, accessor: SpaceDomain.PermissionsSearchResult) {
    const action = menuItem.id as CollectionAccessLevel;
    if (accessor.action !== action) {
      this.addMemberFacade.updateSelectedResultAction(action, accessor);
      if (accessor.type === 'User') {
        this.analyticsService.run('track', 'Menu Selected', {
          changed_user_id: accessor.token,
          location: 'Add Members',
          menu_selection: menuItem.id,
          context: 'collection',
          token: this.collectionId,
        });
      }
      if (accessor.type === 'UserGroup') {
        this.analyticsService.run('track', 'Menu Selected', {
          accessor_id: accessor.token,
          accessor_type: 'UserGroup',
          location: 'Add Members',
          menu_selection: menuItem.id,
          context: 'collection',
          token: this.collectionId,
        });
      }
    }
  }

  handleDone(entitlementsAdded = false) {
    this.done.emit(entitlementsAdded);
  }

  hideError() {
    this.modeAlert = null;
  }

  showError() {
    this.modeAlert = {
      text: 'Some Members could not be added. Please try again.',
      type: ManageAccessAlertType.Error,
    };
  }

  addEntitlements() {
    this.isSaving$.next(true);
    this.dialog.disableClose = true;
    this.hideError();
    this.createEntitlementsRequest();
    this.analyticsService.run('track', 'UI Clicked', {
      location: 'Add Members',
      action: 'Add Members',
      token: this.collectionId,
    });
  }

  createEntitlementsRequest() {
    const current = this.selectedResults$.getValue();

    this.addMemberFacade
      .createEntitlements(this.workspaceName, this.collectionId, current)
      .pipe(
        catchError(() => {
          this.showError();

          return of([]);
        })
      )
      .subscribe((newEntitlements) => {
        this.isSaving$.next(false);
        this.dialog.disableClose = false;

        if (newEntitlements.length) {
          this.handleDone(true);
        }
      });
  }
}
